/**
 * Can this address be pinned to ONE place in the country?
 *
 * KEEP IN LOCKSTEP with `server/amb_build.py::_streetview_resolvable`. Same precedent, and the
 * same reason, as `fieldSchema.ts`'s "KEEP IN LOCKSTEP with validate_subject" note: this rule
 * decides what the agent sees on the review map, the Python twin decides what goes in the signed
 * deliverable, and a report that plots a property the review screen refused to plot (or the
 * reverse) is a document the agent cannot explain. `addressPin.test.ts` asserts both halves agree
 * on the four measured failures below.
 *
 * WHY THE RULE IS THIS STRICT. Google and Cotality both resolve a free-text address NATIONALLY.
 * Measured against the live provider on 2026-07-30, of the first eight bare street lines from a
 * Plano, TX 75024 MLS export, FOUR resolved to a different state:
 *
 *     "4301 Brady Drive"   -> 112 BRADY DR, RADFORD, VA
 *     "4425 Foxtail Lane"  -> 4425 FOXTAIL LN, RICHMOND, VA
 *     "4456 Big Sky Drive" -> 32 DOWNHILL DRIVE, BIG SKY, MT
 *     "6720 Grant Lane"    -> 6720 BLUFF LANE, GATESVILLE, TX
 *
 * A photo of the wrong house is bad. A PIN on the wrong house is worse — a map reads as
 * authoritative and gives the reader nothing to sense-check it against. No ZIP and no state means
 * no pin, and the count of what was left off is disclosed rather than quietly shortening the map.
 */

const ZIP = /\b\d{5}(?:-\d{4})?\b/;
const STATE =
  /\b(?:AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)\b/i;

/**
 * A HOUSE NUMBER IS NOT A ZIP (2026-08-12, found against the live Cotality key). `ZIP` was tested
 * against the whole string, and a five-digit house number is five digits — `ZIP.test("11203 Sesame
 * Street")` was `true`, so most of suburban DFW passed this gate on a bare street line and was
 * geocoded nationally. Strip a leading house number first. The state test is unaffected: a state
 * code cannot be a house number. KEEP IN LOCKSTEP with the Python twin.
 */
const LEADING_HOUSE_NUM = /^\s*\d+(?:-\d+)?[A-Za-z]?\b/;

export function isPinnable(address: string | null | undefined): boolean {
  const a = String(address ?? "").trim();
  if (!a) return false;
  return ZIP.test(a.replace(LEADING_HOUSE_NUM, "")) || STATE.test(a);
}

export interface PinInput {
  address: string | null | undefined;
  kind: "subject" | "comp" | "competing";
  /** B2 (2026-11-02): the row's STORED location (`compMapModel.storedLocation`), when it has one.
   *  A stored coordinate is placed as stored — the same point the comps map shows — and is never
   *  re-geocoded from the address. */
  at?: { lat: number; lon: number; source: string } | null;
  /** The server tried to place this row and refused (`_location.status === "not_mapped"`). It is
   *  listed as not mapped rather than geocoded again in the browser. */
  refused?: boolean;
  /** A competing listing's MLS status ("Active", "Pending", ...). It words the legend only. */
  status?: string | null;
}

export interface Pin {
  address: string;
  kind: "subject" | "comp" | "competing";
  kind_label: string;
  label: string;
  /** Present only when the pin is placed from a stored coordinate; carries its source. */
  lat?: number;
  lon?: number;
  coord_source?: string;
}

/** "Active" or "Pending" for a listing's MLS status. A status that says pending, contingent or
 * under contract reads "Pending"; every other on-market listing reads "Active". Words only. */
export function listingStatusWord(status: string | null | undefined): "Active" | "Pending" {
  return /pend|under\s*contract|contingent|option/i.test(String(status ?? "")) ? "Pending" : "Active";
}

const KIND_LABEL: Record<Pin["kind"], string> = {
  subject: "Your home",
  comp: "Closed sale used in the valuation",
  competing: "On the market now — competition, not a comparable",
};

/** Google's Static Maps labels are ONE character, so numbering runs 1..9. The interactive map has
 * no such limit, but it uses the same numbers anyway — a pin numbered differently on screen and in
 * the report is the same failure as a pin in the wrong place, just harder to notice. */
export const MAX_LABELLED_PINS = 9;

export function buildPins(
  inputs: PinInput[],
): { pins: Pin[]; dropped: number; unlabelled: number; droppedAddresses: string[] } {
  const pins: Pin[] = [];
  // WHICH ONES, NOT JUST HOW MANY (2026-08-10). `dropped` was a count, and a count cannot be
  // displayed usefully: "2 comps not mapped" leaves the agent to work out which two by eye against
  // a grid of five. The addresses are already in hand at the moment we decide not to pin them, so
  // keeping them costs nothing and turns the disclosure into something actionable — the agent can
  // see that the unmapped one is the comp whose address arrived as a bare street line and fix it.
  // The `dropped` count stays for existing callers and is exactly `droppedAddresses.length`.
  const droppedAddresses: string[] = [];
  let unlabelled = 0;
  let n = 0;
  for (const i of inputs) {
    const address = String(i.address ?? "").trim();
    if (!address) continue;
    // A stored coordinate is the location: it needs no ZIP or state on the street line, because
    // nothing is geocoded. Without one, the address gate applies exactly as before.
    if (!i.at && (i.refused || !isPinnable(address))) {
      droppedAddresses.push(address);
      continue;
    }
    const at = i.at ? { lat: i.at.lat, lon: i.at.lon, coord_source: i.at.source } : {};
    if (i.kind === "subject") {
      pins.push({ address, kind: "subject", kind_label: KIND_LABEL.subject, label: "S", ...at });
      continue;
    }
    n += 1;
    if (n > MAX_LABELLED_PINS) {
      unlabelled += 1;
      continue;
    }
    const kindLabel = i.kind === "competing"
      ? `${listingStatusWord(i.status)} listing — competition, not a comparable`
      : KIND_LABEL[i.kind];
    pins.push({ address, kind: i.kind, kind_label: kindLabel, label: String(n), ...at });
  }
  return { pins, dropped: droppedAddresses.length, unlabelled, droppedAddresses };
}
